export interface NormalizedCjPids {
  byUserId: Record<number, string>
  bySegment: Record<string, string>
}

/** Pulls the PID row array from common API envelope shapes. */
const extractCjPidRows = (raw: unknown): unknown[] => {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw !== 'object') return []

  const o = raw as Record<string, unknown>

  // { data: [...] } or { data: [...], total }
  if (Array.isArray(o.data)) return o.data

  // { data: { data: [...], total } }
  const inner = o.data
  if (
    inner &&
    typeof inner === 'object' &&
    Array.isArray((inner as { data?: unknown }).data)
  ) {
    return (inner as { data: unknown[] }).data
  }

  return []
}

/**
 * Normalizes `/admin/brands/user-cj-pids` payloads into:
 *  - `byUserId`: PID per Chirpyest user id (rows with `type === 'userid'`)
 *  - `bySegment`: PID per segment name like `'website'` (rows with `type === 'segment'`)
 */
export const normalizeUserCjPidsResponse = (raw: unknown): NormalizedCjPids => {
  const rows = extractCjPidRows(raw)
  const byUserId: Record<number, string> = {}
  const bySegment: Record<string, string> = {}

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const r = row as Record<string, unknown>

    const type = r.type
    const name = r.name
    const pid = r.pid
    const pidStr = pid == null ? '' : String(pid)
    if (!pidStr) continue

    if (type === 'userid' && name != null) {
      const uid = Number(name)
      if (Number.isFinite(uid)) {
        byUserId[uid] = pidStr
      }
      continue
    }

    if (type === 'segment' && typeof name === 'string' && name !== '') {
      bySegment[name] = pidStr
      continue
    }

    // Legacy row shapes (no `type` field)
    const legacyUid = r.userId ?? r.user_id
    const legacyPid = r.cjPid ?? r.cjpid ?? r.pid ?? r.CJPid
    if (legacyUid != null && legacyPid != null) {
      const uid = Number(legacyUid)
      const lpid = String(legacyPid)
      if (Number.isFinite(uid) && lpid !== '') {
        byUserId[uid] = lpid
      }
    }
  }

  return { byUserId, bySegment }
}
